import type { Mark, MarkType, Node } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { AddMarkStep, RemoveMarkStep } from "@tiptap/pm/transform";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export const FILE_LINK_ICON_CLASS = "file-link-icon";
export const FILE_LINK_ICON_SELECTED_CLASS = "file-link-icon-selected";

const fileLinkIconKey = new PluginKey<DecorationSet>("fileLinkIcon");

// The icon sits on the link's first character rather than on gr-file: a decoration renders inside every mark,
// so the comment highlight and the selection class reach the icon too.
const findFileLinkIcons = (root: Node, offset: number, fileType: MarkType) => {
	const decorations: Decoration[] = [];
	let previousLink: Mark | undefined;

	root.descendants((node, pos) => {
		if (!node.isInline || !node.isLeaf) {
			previousLink = undefined;
			return true;
		}
		const link = fileType.isInSet(node.marks);
		if (link && node.isText && !previousLink?.eq(link)) {
			// An emoji or another astral first character takes two positions: one would split its surrogate pair
			const firstCharLength = String.fromCodePoint(node.text.codePointAt(0)).length;
			const from = offset + pos;
			decorations.push(Decoration.inline(from, from + firstCharLength, { class: FILE_LINK_ICON_CLASS }));
		}
		previousLink = link;
		return false;
	});

	return decorations;
};

// Rescans only the textblocks a transaction touched, so typing does not walk the whole document
const updateFileLinkIcons = (tr: Transaction, icons: DecorationSet, fileType: MarkType) => {
	let next = icons.map(tr.mapping, tr.doc);
	const docSize = tr.doc.content.size;
	tr.steps.forEach((step, index) => {
		const rest = tr.mapping.slice(index + 1);
		const ranges: [number, number][] = [];
		step.getMap().forEach((_oldStart, _oldEnd, newStart, newEnd) => ranges.push([newStart, newEnd]));
		// A mark step moves no positions, so its StepMap is empty
		if (step instanceof AddMarkStep || step instanceof RemoveMarkStep) ranges.push([step.from, step.to]);
		for (const [start, end] of ranges) {
			const from = Math.max(0, rest.map(start, -1) - 1);
			const to = Math.min(docSize, rest.map(end, 1) + 1);
			tr.doc.nodesBetween(from, to, (node, pos) => {
				if (!node.isTextblock) return true;
				const blockEnd = pos + node.nodeSize;
				next = next
					.remove(next.find(pos + 1, blockEnd - 1))
					.add(tr.doc, findFileLinkIcons(node, pos + 1, fileType));
				return false;
			});
		}
	});
	return next;
};

export const fileLinkIconPlugin = (fileType: MarkType) =>
	new Plugin<DecorationSet>({
		key: fileLinkIconKey,
		state: {
			init: (_, state) => DecorationSet.create(state.doc, findFileLinkIcons(state.doc, 0, fileType)),
			apply: (tr, icons) => (tr.docChanged ? updateFileLinkIcons(tr, icons, fileType) : icons),
		},
		props: {
			decorations(state) {
				const icons = fileLinkIconKey.getState(state);
				const { from, to, empty } = state.selection;
				if (empty) return icons;

				const selected = icons
					.find(from, to, () => true)
					.filter((icon) => icon.from >= from && icon.from < to)
					.map((icon) => Decoration.inline(icon.from, icon.to, { class: FILE_LINK_ICON_SELECTED_CLASS }));
				return selected.length ? icons.add(state.doc, selected) : icons;
			},
		},
	});
